/**
 * Composable for translating hex color codes to human-readable color descriptions
 * Supports eyes, hair, and skin color translations with high sensitivity
 */

// Eye colors - more granular and descriptive
const EYE_COLORS = [
  // Browns
  { name: 'Deep Black', hex: '#0A0A0A', ranges: [{ h: [0, 360], s: [0, 40], l: [0, 8] }] },
  { name: 'Black', hex: '#1A1A1A', ranges: [{ h: [0, 360], s: [0, 35], l: [8, 15] }] },
  { name: 'Very Dark Brown', hex: '#2D1B0E', ranges: [{ h: [15, 45], s: [30, 100], l: [10, 18] }] },
  { name: 'Dark Brown', hex: '#3D2817', ranges: [{ h: [15, 45], s: [35, 100], l: [15, 25] }] },
  { name: 'Brown', hex: '#6B4423', ranges: [{ h: [20, 40], s: [40, 80], l: [22, 35] }] },
  { name: 'Light Brown', hex: '#8B5A2B', ranges: [{ h: [25, 40], s: [50, 80], l: [32, 42] }] },
  { name: 'Honey Brown', hex: '#A0713D', ranges: [{ h: [28, 42], s: [45, 75], l: [38, 50] }] },
  
  // Amber/Hazel
  { name: 'Dark Amber', hex: '#B8860B', ranges: [{ h: [38, 48], s: [70, 100], l: [35, 48] }] },
  { name: 'Amber', hex: '#D4A017', ranges: [{ h: [40, 50], s: [75, 100], l: [43, 58] }] },
  { name: 'Golden Amber', hex: '#FFBF00', ranges: [{ h: [45, 52], s: [85, 100], l: [48, 62] }] },
  { name: 'Dark Hazel', hex: '#6B5D3F', ranges: [{ h: [40, 65], s: [30, 60], l: [28, 40] }] },
  { name: 'Hazel', hex: '#8E7618', ranges: [{ h: [42, 62], s: [45, 75], l: [32, 48] }] },
  { name: 'Green Hazel', hex: '#9A8B3F', ranges: [{ h: [48, 75], s: [40, 70], l: [38, 52] }] },
  
  // Greens
  { name: 'Dark Green', hex: '#1C4220', ranges: [{ h: [100, 140], s: [40, 80], l: [15, 25] }] },
  { name: 'Forest Green', hex: '#228B22', ranges: [{ h: [105, 135], s: [50, 90], l: [25, 38] }] },
  { name: 'Green', hex: '#2E8B36', ranges: [{ h: [110, 140], s: [45, 85], l: [32, 48] }] },
  { name: 'Jade Green', hex: '#3FA34D', ranges: [{ h: [120, 145], s: [50, 80], l: [40, 55] }] },
  { name: 'Light Green', hex: '#5CB85C', ranges: [{ h: [120, 150], s: [45, 75], l: [50, 65] }] },
  { name: 'Sea Green', hex: '#3CB371', ranges: [{ h: [140, 165], s: [45, 75], l: [42, 58] }] },
  
  // Blues
  { name: 'Navy Blue', hex: '#000080', ranges: [{ h: [220, 250], s: [70, 100], l: [15, 28] }] },
  { name: 'Dark Blue', hex: '#00008B', ranges: [{ h: [215, 245], s: [80, 100], l: [25, 35] }] },
  { name: 'Sapphire Blue', hex: '#0F52BA', ranges: [{ h: [210, 230], s: [75, 100], l: [35, 48] }] },
  { name: 'Blue', hex: '#1E90FF', ranges: [{ h: [200, 220], s: [80, 100], l: [50, 62] }] },
  { name: 'Bright Blue', hex: '#4169E1', ranges: [{ h: [220, 235], s: [70, 95], l: [52, 65] }] },
  { name: 'Sky Blue', hex: '#5DADE2', ranges: [{ h: [195, 210], s: [55, 85], l: [60, 72] }] },
  { name: 'Light Blue', hex: '#87CEEB', ranges: [{ h: [190, 210], s: [50, 80], l: [68, 82] }] },
  { name: 'Ice Blue', hex: '#B0E0E6', ranges: [{ h: [180, 200], s: [40, 70], l: [78, 88] }] },
  
  // Blue-Greens
  { name: 'Teal', hex: '#008080', ranges: [{ h: [170, 190], s: [70, 100], l: [20, 35] }] },
  { name: 'Aqua', hex: '#00CED1', ranges: [{ h: [175, 188], s: [80, 100], l: [38, 52] }] },
  { name: 'Turquoise', hex: '#40E0D0', ranges: [{ h: [165, 185], s: [70, 95], l: [52, 68] }] },
  
  // Grays
  { name: 'Charcoal Gray', hex: '#4A4A4A', ranges: [{ h: [0, 360], s: [0, 12], l: [25, 35] }] },
  { name: 'Dark Gray', hex: '#696969', ranges: [{ h: [0, 360], s: [0, 15], l: [35, 45] }] },
  { name: 'Gray', hex: '#808080', ranges: [{ h: [0, 360], s: [0, 18], l: [43, 55] }] },
  { name: 'Light Gray', hex: '#A9A9A9', ranges: [{ h: [0, 360], s: [0, 15], l: [52, 68] }] },
  { name: 'Silver Gray', hex: '#C0C0C0', ranges: [{ h: [0, 360], s: [0, 12], l: [65, 78] }] },
  
  // Violets/Purples
  { name: 'Deep Violet', hex: '#4B0082', ranges: [{ h: [270, 285], s: [80, 100], l: [20, 32] }] },
  { name: 'Violet', hex: '#8B00FF', ranges: [{ h: [275, 290], s: [85, 100], l: [35, 52] }] },
  { name: 'Purple', hex: '#9370DB', ranges: [{ h: [260, 280], s: [50, 80], l: [55, 70] }] },
  { name: 'Lavender', hex: '#B19CD9', ranges: [{ h: [265, 285], s: [35, 65], l: [65, 78] }] },
  
  // Reds
  { name: 'Deep Red', hex: '#8B0000', ranges: [{ h: [0, 10], s: [85, 100], l: [20, 32] }, { h: [350, 360], s: [85, 100], l: [20, 32] }] },
  { name: 'Dark Red', hex: '#B22222', ranges: [{ h: [0, 12], s: [70, 95], l: [30, 42] }, { h: [348, 360], s: [70, 95], l: [30, 42] }] },
  { name: 'Red', hex: '#DC143C', ranges: [{ h: [345, 360], s: [75, 100], l: [40, 55] }, { h: [0, 12], s: [75, 100], l: [40, 55] }] },
  { name: 'Crimson', hex: '#DC143C', ranges: [{ h: [348, 360], s: [75, 95], l: [42, 52] }, { h: [0, 8], s: [75, 95], l: [42, 52] }] },
  
  // Pinks
  { name: 'Rose', hex: '#FF007F', ranges: [{ h: [320, 340], s: [80, 100], l: [45, 60] }] },
  { name: 'Pink', hex: '#FF69B4', ranges: [{ h: [325, 345], s: [70, 95], l: [58, 72] }] },
  
  // Golds
  { name: 'Golden', hex: '#FFD700', ranges: [{ h: [48, 55], s: [90, 100], l: [48, 62] }] }
]

// Hair colors - comprehensive natural and fantasy palette
const HAIR_COLORS = [
  // Blacks
  { name: 'Jet Black', hex: '#0A0A0A', ranges: [{ h: [0, 360], s: [0, 25], l: [0, 8] }] },
  { name: 'Black', hex: '#1A1A1A', ranges: [{ h: [0, 360], s: [0, 35], l: [8, 18] }] },
  { name: 'Soft Black', hex: '#28282B', ranges: [{ h: [0, 360], s: [0, 30], l: [15, 22] }] },
  
  // Dark Browns
  { name: 'Deepest Brown', hex: '#2B1810', ranges: [{ h: [15, 40], s: [35, 75], l: [8, 15] }] },
  { name: 'Very Dark Brown', hex: '#3D2817', ranges: [{ h: [18, 42], s: [40, 80], l: [12, 22] }] },
  { name: 'Dark Brown', hex: '#4A2F1A', ranges: [{ h: [20, 42], s: [45, 80], l: [18, 28] }] },
  { name: 'Espresso', hex: '#5C3317', ranges: [{ h: [22, 38], s: [50, 85], l: [22, 32] }] },
  
  // Medium Browns
  { name: 'Brown', hex: '#6B4423', ranges: [{ h: [22, 38], s: [45, 75], l: [28, 38] }] },
  { name: 'Chocolate Brown', hex: '#7B3F00', ranges: [{ h: [25, 35], s: [80, 100], l: [22, 35] }] },
  { name: 'Chestnut', hex: '#8B4513', ranges: [{ h: [20, 32], s: [60, 90], l: [28, 42] }] },
  { name: 'Medium Brown', hex: '#8B5A2B', ranges: [{ h: [28, 40], s: [50, 75], l: [35, 48] }] },
  { name: 'Caramel', hex: '#AF6E4D', ranges: [{ h: [22, 35], s: [45, 70], l: [45, 58] }] },
  
  // Light Browns
  { name: 'Light Brown', hex: '#B5824A', ranges: [{ h: [28, 42], s: [45, 70], l: [48, 60] }] },
  { name: 'Tawny', hex: '#CD8032', ranges: [{ h: [25, 38], s: [55, 80], l: [48, 62] }] },
  { name: 'Honey Brown', hex: '#D2691E', ranges: [{ h: [28, 42], s: [65, 85], l: [45, 58] }] },
  
  // Auburn/Reddish Browns
  { name: 'Dark Auburn', hex: '#7C2C2C', ranges: [{ h: [0, 18], s: [50, 75], l: [28, 40] }, { h: [350, 360], s: [50, 75], l: [28, 40] }] },
  { name: 'Auburn', hex: '#A52A2A', ranges: [{ h: [0, 22], s: [60, 85], l: [35, 50] }, { h: [345, 360], s: [60, 85], l: [35, 50] }] },
  { name: 'Mahogany', hex: '#C04000', ranges: [{ h: [15, 28], s: [85, 100], l: [35, 48] }] },
  { name: 'Burnt Sienna', hex: '#E97451', ranges: [{ h: [10, 22], s: [70, 90], l: [58, 70] }] },
  
  // Reds
  { name: 'Deep Red', hex: '#8B0000', ranges: [{ h: [0, 8], s: [85, 100], l: [22, 35] }, { h: [352, 360], s: [85, 100], l: [22, 35] }] },
  { name: 'Dark Red', hex: '#B22222', ranges: [{ h: [0, 10], s: [75, 95], l: [32, 45] }, { h: [350, 360], s: [75, 95], l: [32, 45] }] },
  { name: 'Red', hex: '#DC143C', ranges: [{ h: [348, 360], s: [75, 95], l: [42, 55] }, { h: [0, 10], s: [75, 95], l: [42, 55] }] },
  { name: 'Bright Red', hex: '#FF0000', ranges: [{ h: [0, 8], s: [90, 100], l: [48, 58] }, { h: [352, 360], s: [90, 100], l: [48, 58] }] },
  { name: 'Cherry Red', hex: '#DE3163', ranges: [{ h: [348, 360], s: [70, 90], l: [52, 65] }, { h: [0, 8], s: [70, 90], l: [52, 65] }] },
  
  // Gingers/Coppers
  { name: 'Dark Ginger', hex: '#CC5500', ranges: [{ h: [18, 28], s: [85, 100], l: [35, 48] }] },
  { name: 'Ginger', hex: '#FF6347', ranges: [{ h: [5, 18], s: [85, 100], l: [58, 70] }] },
  { name: 'Copper', hex: '#B87333', ranges: [{ h: [25, 35], s: [55, 80], l: [42, 55] }] },
  { name: 'Strawberry Blonde', hex: '#FF9966', ranges: [{ h: [20, 32], s: [80, 100], l: [68, 78] }] },
  
  // Dirty/Ash Blondes
  { name: 'Dark Blonde', hex: '#B5935A', ranges: [{ h: [38, 48], s: [35, 60], l: [50, 62] }] },
  { name: 'Dirty Blonde', hex: '#D4C2A8', ranges: [{ h: [35, 48], s: [25, 50], l: [62, 75] }] },
  { name: 'Ash Blonde', hex: '#C3B091', ranges: [{ h: [38, 50], s: [25, 45], l: [62, 75] }] },
  { name: 'Sandy Blonde', hex: '#E4C68B', ranges: [{ h: [40, 50], s: [50, 75], l: [68, 78] }] },
  
  // Blondes
  { name: 'Blonde', hex: '#F4E7C3', ranges: [{ h: [42, 52], s: [50, 80], l: [78, 88] }] },
  { name: 'Golden Blonde', hex: '#F0E68C', ranges: [{ h: [50, 58], s: [70, 90], l: [72, 85] }] },
  { name: 'Light Blonde', hex: '#FFF8DC', ranges: [{ h: [45, 58], s: [50, 90], l: [85, 92] }] },
  { name: 'Platinum Blonde', hex: '#F5F5DC', ranges: [{ h: [50, 60], s: [30, 60], l: [88, 95] }] },
  { name: 'Champagne Blonde', hex: '#FAF0E6', ranges: [{ h: [30, 45], s: [35, 65], l: [90, 96] }] },
  
  // Grays/Silvers
  { name: 'Dark Gray', hex: '#696969', ranges: [{ h: [0, 360], s: [0, 15], l: [38, 48] }] },
  { name: 'Gray', hex: '#808080', ranges: [{ h: [0, 360], s: [0, 18], l: [45, 58] }] },
  { name: 'Light Gray', hex: '#A9A9A9', ranges: [{ h: [0, 360], s: [0, 18], l: [55, 68] }] },
  { name: 'Silver', hex: '#C0C0C0', ranges: [{ h: [0, 360], s: [0, 22], l: [68, 78] }] },
  { name: 'Platinum Silver', hex: '#E5E4E2', ranges: [{ h: [0, 360], s: [0, 15], l: [85, 92] }] },
  { name: 'White', hex: '#F5F5F5', ranges: [{ h: [0, 360], s: [0, 12], l: [90, 100] }] },
  
  // Fantasy Blues
  { name: 'Dark Blue', hex: '#00008B', ranges: [{ h: [220, 245], s: [80, 100], l: [22, 35] }] },
  { name: 'Midnight Blue', hex: '#191970', ranges: [{ h: [235, 250], s: [60, 85], l: [25, 38] }] },
  { name: 'Royal Blue', hex: '#4169E1', ranges: [{ h: [220, 235], s: [65, 90], l: [50, 65] }] },
  { name: 'Blue', hex: '#0000FF', ranges: [{ h: [235, 245], s: [90, 100], l: [45, 58] }] },
  { name: 'Sky Blue', hex: '#87CEEB', ranges: [{ h: [195, 210], s: [60, 85], l: [68, 80] }] },
  { name: 'Ice Blue', hex: '#B0E0E6', ranges: [{ h: [180, 200], s: [45, 75], l: [78, 88] }] },
  
  // Fantasy Greens
  { name: 'Dark Green', hex: '#006400', ranges: [{ h: [115, 135], s: [85, 100], l: [15, 28] }] },
  { name: 'Forest Green', hex: '#228B22', ranges: [{ h: [110, 130], s: [65, 90], l: [28, 42] }] },
  { name: 'Green', hex: '#32CD32', ranges: [{ h: [115, 135], s: [70, 95], l: [48, 62] }] },
  { name: 'Lime Green', hex: '#7FFF00', ranges: [{ h: [75, 95], s: [90, 100], l: [48, 62] }] },
  { name: 'Mint Green', hex: '#98FF98', ranges: [{ h: [110, 135], s: [70, 100], l: [78, 88] }] },
  
  // Fantasy Purples/Pinks
  { name: 'Deep Purple', hex: '#4B0082', ranges: [{ h: [270, 285], s: [85, 100], l: [20, 32] }] },
  { name: 'Dark Purple', hex: '#663399', ranges: [{ h: [270, 285], s: [60, 85], l: [35, 48] }] },
  { name: 'Purple', hex: '#9370DB', ranges: [{ h: [265, 280], s: [50, 75], l: [58, 72] }] },
  { name: 'Lavender', hex: '#B19CD9', ranges: [{ h: [270, 285], s: [40, 65], l: [72, 82] }] },
  { name: 'Violet', hex: '#8B00FF', ranges: [{ h: [280, 295], s: [85, 100], l: [45, 58] }] },
  { name: 'Magenta', hex: '#FF00FF', ranges: [{ h: [295, 305], s: [90, 100], l: [45, 58] }] },
  { name: 'Hot Pink', hex: '#FF69B4', ranges: [{ h: [325, 340], s: [80, 100], l: [68, 80] }] },
  { name: 'Pink', hex: '#FFC0CB', ranges: [{ h: [345, 360], s: [70, 100], l: [85, 92] }, { h: [0, 10], s: [70, 100], l: [85, 92] }] },
  { name: 'Rose', hex: '#FF007F', ranges: [{ h: [320, 338], s: [85, 100], l: [45, 60] }] },
  
  // Fantasy Others
  { name: 'Teal', hex: '#008080', ranges: [{ h: [175, 190], s: [80, 100], l: [22, 35] }] },
  { name: 'Turquoise', hex: '#40E0D0', ranges: [{ h: [170, 185], s: [70, 95], l: [52, 68] }] },
  { name: 'Orange', hex: '#FF8C00', ranges: [{ h: [30, 42], s: [90, 100], l: [48, 62] }] },
  { name: 'Yellow', hex: '#FFFF00', ranges: [{ h: [55, 65], s: [95, 100], l: [48, 60] }] },
  { name: 'Gold', hex: '#FFD700', ranges: [{ h: [48, 55], s: [90, 100], l: [48, 62] }] }
]

// Skin tones - realistic human diversity + fantasy
const SKIN_COLORS = [
  // Very Light/Pale
  { name: 'Porcelain', hex: '#FFF5F0', ranges: [{ h: [0, 35], s: [20, 50], l: [94, 100] }] },
  { name: 'Ivory', hex: '#FFF8E7', ranges: [{ h: [35, 50], s: [30, 60], l: [92, 98] }] },
  { name: 'Very Pale', hex: '#FCECD7', ranges: [{ h: [25, 45], s: [35, 65], l: [88, 94] }] },
  { name: 'Alabaster', hex: '#FAE7D9', ranges: [{ h: [22, 40], s: [40, 70], l: [85, 92] }] },
  
  // Pale/Fair
  { name: 'Pale', hex: '#F3D6B6', ranges: [{ h: [28, 45], s: [45, 75], l: [78, 88] }] },
  { name: 'Fair', hex: '#E8C4A0', ranges: [{ h: [28, 45], s: [50, 75], l: [70, 82] }] },
  { name: 'Cream', hex: '#FFFACD', ranges: [{ h: [48, 62], s: [50, 100], l: [88, 96] }] },
  { name: 'Beige', hex: '#F5DEB3', ranges: [{ h: [38, 52], s: [55, 85], l: [80, 88] }] },
  
  // Light
  { name: 'Light', hex: '#D4A574', ranges: [{ h: [28, 42], s: [45, 70], l: [60, 72] }] },
  { name: 'Peach', hex: '#FFDAB9', ranges: [{ h: [22, 35], s: [70, 100], l: [82, 90] }] },
  { name: 'Rose Beige', hex: '#ECC8AF', ranges: [{ h: [22, 38], s: [50, 75], l: [75, 85] }] },
  { name: 'Sand', hex: '#D2B48C', ranges: [{ h: [35, 48], s: [40, 65], l: [65, 78] }] },
  
  // Medium Light
  { name: 'Light Medium', hex: '#C9A882', ranges: [{ h: [32, 45], s: [40, 65], l: [60, 70] }] },
  { name: 'Wheat', hex: '#C68F62', ranges: [{ h: [25, 38], s: [45, 70], l: [55, 65] }] },
  { name: 'Honey', hex: '#D2A97A', ranges: [{ h: [30, 42], s: [50, 75], l: [62, 72] }] },
  { name: 'Golden', hex: '#C9A961', ranges: [{ h: [40, 52], s: [50, 75], l: [55, 68] }] },
  
  // Medium
  { name: 'Medium', hex: '#C68F62', ranges: [{ h: [22, 38], s: [42, 68], l: [50, 62] }] },
  { name: 'Tawny', hex: '#B67B5D', ranges: [{ h: [20, 35], s: [40, 65], l: [48, 58] }] },
  { name: 'Tan', hex: '#A67C52', ranges: [{ h: [25, 40], s: [38, 62], l: [42, 54] }] },
  { name: 'Bronze', hex: '#967348', ranges: [{ h: [28, 40], s: [45, 70], l: [38, 50] }] },
  
  // Medium Dark
  { name: 'Caramel', hex: '#8D5524', ranges: [{ h: [25, 38], s: [60, 85], l: [32, 45] }] },
  { name: 'Almond', hex: '#927465', ranges: [{ h: [15, 32], s: [30, 55], l: [45, 58] }] },
  { name: 'Cinnamon', hex: '#8B6F47', ranges: [{ h: [32, 48], s: [35, 60], l: [38, 50] }] },
  { name: 'Olive', hex: '#7F6F4C', ranges: [{ h: [40, 60], s: [30, 55], l: [35, 48] }] },
  
  // Brown
  { name: 'Brown', hex: '#6B4423', ranges: [{ h: [22, 40], s: [48, 75], l: [25, 38] }] },
  { name: 'Chestnut', hex: '#654321', ranges: [{ h: [25, 38], s: [50, 75], l: [22, 32] }] },
  { name: 'Mahogany', hex: '#5C3317', ranges: [{ h: [20, 35], s: [55, 80], l: [20, 30] }] },
  { name: 'Sienna', hex: '#704214', ranges: [{ h: [25, 38], s: [65, 88], l: [22, 32] }] },
  
  // Dark Brown
  { name: 'Dark Brown', hex: '#4A2F1A', ranges: [{ h: [22, 40], s: [48, 75], l: [18, 28] }] },
  { name: 'Cocoa', hex: '#3D2817', ranges: [{ h: [20, 38], s: [45, 72], l: [15, 25] }] },
  { name: 'Deep Brown', hex: '#36241C', ranges: [{ h: [18, 35], s: [40, 68], l: [12, 22] }] },
  { name: 'Coffee', hex: '#311C17', ranges: [{ h: [15, 32], s: [42, 70], l: [10, 18] }] },
  
  // Very Dark
  { name: 'Ebony', hex: '#2B1810', ranges: [{ h: [18, 38], s: [38, 68], l: [8, 15] }] },
  { name: 'Dark Ebony', hex: '#1F120C', ranges: [{ h: [15, 35], s: [35, 65], l: [5, 12] }] },
  { name: 'Midnight', hex: '#14100B', ranges: [{ h: [18, 40], s: [30, 60], l: [3, 10] }] },
  
  // Fantasy - Grays
  { name: 'Pale Gray', hex: '#D3D3D3', ranges: [{ h: [0, 360], s: [0, 15], l: [78, 88] }] },
  { name: 'Light Gray', hex: '#B8B8B8', ranges: [{ h: [0, 360], s: [0, 18], l: [68, 80] }] },
  { name: 'Ashen Gray', hex: '#9E9E9E', ranges: [{ h: [0, 360], s: [0, 18], l: [58, 70] }] },
  { name: 'Slate Gray', hex: '#708090', ranges: [{ h: [195, 215], s: [10, 30], l: [48, 60] }] },
  { name: 'Dark Gray', hex: '#4F4F4F', ranges: [{ h: [0, 360], s: [0, 15], l: [28, 38] }] },
  
  // Fantasy - Blues
  { name: 'Ice Blue', hex: '#D4E4F7', ranges: [{ h: [210, 225], s: [35, 65], l: [85, 93] }] },
  { name: 'Pale Blue', hex: '#B0C4DE', ranges: [{ h: [210, 225], s: [35, 60], l: [75, 85] }] },
  { name: 'Sky Blue', hex: '#87CEEB', ranges: [{ h: [195, 210], s: [50, 75], l: [70, 82] }] },
  { name: 'Blue-Gray', hex: '#6B8CA8', ranges: [{ h: [200, 220], s: [25, 45], l: [52, 68] }] },
  { name: 'Steel Blue', hex: '#4682B4', ranges: [{ h: [200, 215], s: [45, 68], l: [48, 62] }] },
  
  // Fantasy - Greens
  { name: 'Mint', hex: '#C5E8D5', ranges: [{ h: [140, 160], s: [30, 55], l: [80, 90] }] },
  { name: 'Pale Green', hex: '#B4D7B8', ranges: [{ h: [115, 140], s: [30, 55], l: [72, 85] }] },
  { name: 'Greenish', hex: '#8BA882', ranges: [{ h: [105, 130], s: [20, 40], l: [58, 72] }] },
  { name: 'Moss Green', hex: '#6B8E6B', ranges: [{ h: [110, 130], s: [25, 45], l: [45, 58] }] },
  
  // Fantasy - Purples/Pinks
  { name: 'Pale Lavender', hex: '#E6E6FA', ranges: [{ h: [230, 250], s: [35, 65], l: [92, 98] }] },
  { name: 'Lavender', hex: '#D8BFD8', ranges: [{ h: [285, 310], s: [25, 45], l: [78, 88] }] },
  { name: 'Lilac', hex: '#C8A2C8', ranges: [{ h: [290, 310], s: [30, 55], l: [68, 80] }] },
  { name: 'Rose', hex: '#FADADD', ranges: [{ h: [345, 360], s: [55, 85], l: [88, 95] }, { h: [0, 10], s: [55, 85], l: [88, 95] }] },
  
  // Fantasy - Golds/Oranges
  { name: 'Gold', hex: '#FFD700', ranges: [{ h: [48, 54], s: [90, 100], l: [48, 58] }] },
  { name: 'Golden Bronze', hex: '#CD7F32', ranges: [{ h: [25, 35], s: [65, 85], l: [45, 58] }] },
  
  // Fantasy - Reds
  { name: 'Crimson', hex: '#DC143C', ranges: [{ h: [348, 360], s: [75, 95], l: [45, 58] }, { h: [0, 10], s: [75, 95], l: [45, 58] }] }
]

/**
 * Convert hex color to HSL
 * @param {string} hex - Hex color code (e.g., '#FF0000')
 * @returns {{h: number, s: number, l: number}} HSL values (h: 0-360, s: 0-100, l: 0-100)
 */
function hexToHSL(hex) {
  // Remove # if present
  hex = hex.replace('#', '')
  
  // Convert to RGB
  let r, g, b
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16) / 255
    g = parseInt(hex[1] + hex[1], 16) / 255
    b = parseInt(hex[2] + hex[2], 16) / 255
  } else {
    r = parseInt(hex.substring(0, 2), 16) / 255
    g = parseInt(hex.substring(2, 4), 16) / 255
    b = parseInt(hex.substring(4, 6), 16) / 255
  }

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h, s, l = (max + min) / 2

  if (max === min) {
    h = s = 0 // achromatic
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  }
}

/**
 * Check if HSL values match a color range
 * @param {{h: number, s: number, l: number}} hsl - HSL values
 * @param {Array} ranges - Array of range objects with h, s, l properties
 * @returns {boolean} True if HSL matches any of the ranges
 */
function matchesRange(hsl, ranges) {
  return ranges.some(range => {
    const hMatch = (hsl.h >= range.h[0] && hsl.h <= range.h[1])
    const sMatch = (hsl.s >= range.s[0] && hsl.s <= range.s[1])
    const lMatch = (hsl.l >= range.l[0] && hsl.l <= range.l[1])
    return hMatch && sMatch && lMatch
  })
}

/**
 * Find the closest color match by calculating color distance
 * @param {{h: number, s: number, l: number}} hsl - HSL values
 * @param {Array} colorList - Array of color objects
 * @returns {string} Human-readable color name
 */
function findClosestColor(hsl, colorList) {
  let closestColor = colorList[0].name
  let minDistance = Infinity

  colorList.forEach(color => {
    const colorHSL = hexToHSL(color.hex)
    
    // Calculate weighted Euclidean distance in HSL space
    // Hue wraps around, so we need special handling
    let hDiff = Math.abs(hsl.h - colorHSL.h)
    if (hDiff > 180) hDiff = 360 - hDiff
    
    // Weight: Hue is most important, then saturation, then lightness
    const distance = Math.sqrt(
      Math.pow(hDiff * 2, 2) + 
      Math.pow(hsl.s - colorHSL.s, 2) + 
      Math.pow(hsl.l - colorHSL.l, 2)
    )

    if (distance < minDistance) {
      minDistance = distance
      closestColor = color.name
    }
  })

  return closestColor
}

/**
 * Translate hex color to human-readable description
 * @param {string} hex - Hex color code
 * @param {'eyes'|'hair'|'skin'} type - Type of color to translate
 * @returns {string} Human-readable color name
 */
export function hexToHumanReadable(hex, type) {
  if (!hex || typeof hex !== 'string') return ''
  
  // If it's already not a hex code, return as-is
  if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(hex)) {
    return hex
  }

  const hsl = hexToHSL(hex)
  let colorList

  switch (type) {
    case 'eyes':
      colorList = EYE_COLORS
      break
    case 'hair':
      colorList = HAIR_COLORS
      break
    case 'skin':
      colorList = SKIN_COLORS
      break
    default:
      return hex
  }

  // First try to find exact range match
  for (const color of colorList) {
    if (matchesRange(hsl, color.ranges)) {
      return color.name
    }
  }

  // If no range match, find closest color
  return findClosestColor(hsl, colorList)
}

/**
 * Get default hex color for a human-readable color name
 * @param {string} name - Human-readable color name
 * @param {'eyes'|'hair'|'skin'} type - Type of color
 * @returns {string|null} Hex color code or null if not found
 */
export function humanReadableToHex(name, type) {
  if (!name || typeof name !== 'string') return null
  
  let colorList
  switch (type) {
    case 'eyes':
      colorList = EYE_COLORS
      break
    case 'hair':
      colorList = HAIR_COLORS
      break
    case 'skin':
      colorList = SKIN_COLORS
      break
    default:
      return null
  }

  const color = colorList.find(c => c.name.toLowerCase() === name.toLowerCase())
  return color ? color.hex : null
}

/**
 * Get all available color names for a type
 * @param {'eyes'|'hair'|'skin'} type - Type of color
 * @returns {Array<string>} Array of human-readable color names
 */
export function getAvailableColors(type) {
  let colorList
  switch (type) {
    case 'eyes':
      colorList = EYE_COLORS
      break
    case 'hair':
      colorList = HAIR_COLORS
      break
    case 'skin':
      colorList = SKIN_COLORS
      break
    default:
      return []
  }
  return colorList.map(c => c.name)
}

export function useColorTranslator() {
  return {
    hexToHumanReadable,
    humanReadableToHex,
    getAvailableColors
  }
}
